'use client';

import { useCalculatedAttributeResolver } from '@hyphengroup/cms-components/calculatedAttributes/useCalculatedAttributeResolver';
import { useCtaResolver } from '@hyphengroup/cms-components/components/ApplyCta';
import ProductCardGrid from '@hyphengroup/cms-components/components/ProductCardGrid';
import { usePathname } from 'next/navigation';
import { useMemo } from 'react';
import type {
  ProductCardGridItems,
  UseProductCardGridItemsParams,
} from './types';
import {
  buildBaseTrackingPayload,
  getProductCardGridRewardCampaign,
  mapProductCardGridItemToCardProps,
} from './utils';
import type { ComponentProductCardGrid } from '$/(cms)/_modules/cmsService/types';
import { useProductDetailModal } from '$/components/cms/helpers/useProductDetailModal';
import { pushDataLayer } from '$/components/cms/pushDataLayer';
import { sessionAttributionTracker } from '$/modules/sessionAttributionTracking/sessionAttributionTracker';

const useProductCardGridItems = ({
  items,
  pathname,
  openProductDetail,
}: UseProductCardGridItemsParams): ProductCardGridItems => {
  const resolve = useCtaResolver();
  // Hooks cannot run inside the map below, so the facade is taken once here and
  // threaded down — the same shape Table Builder uses (CMS-1890).
  const resolveCalculated = useCalculatedAttributeResolver();
  return useMemo(() => {
    return items
      .filter((item) => item.product)
      .map((item, index) => {
        const product = item.product;
        const productId = product?.uid ?? product?.title ?? '';
        const productName = product?.product_name ?? '';
        const providerName = product?.provider?.provider_name ?? '';
        const { rewardCampaignId, rewardCampaignName } =
          getProductCardGridRewardCampaign(item);

        const applyCtaUrl = item.applyCtaUrl || resolve({ product });

        const hasViewDetailsOverride = Boolean(item.viewDetailsCtaUrl?.trim());
        const canOpenPdpModal = Boolean(
          !hasViewDetailsOverride && product?.url_alias,
        );
        const hasViewDetails = Boolean(
          item.viewDetailsCtaUrl || canOpenPdpModal,
        );

        return mapProductCardGridItemToCardProps(
          item,
          undefined,
          {
            applyCtaUrl,
            onPrimaryCtaClick: applyCtaUrl
              ? () => {
                  sessionAttributionTracker.recordApplyClick();
                  pushDataLayer({
                    ...buildBaseTrackingPayload({
                      event: 'product_click_apply',
                      componentTitle: 'Product Card Grid/CTA_Apply Now',
                      index,
                      pathname,
                      productId,
                      productName,
                      providerName,
                      rewardCampaignId,
                      rewardCampaignName,
                    }),
                  });
                }
              : undefined,
            viewDetailsCtaUrl: item.viewDetailsCtaUrl,
            onSecondaryCtaClick: hasViewDetails
              ? () => {
                  pushDataLayer({
                    ...buildBaseTrackingPayload({
                      event: 'product_interaction',
                      componentTitle: 'Product Card Grid/Footer_View Details',
                      index,
                      pathname,
                      productId,
                      productName,
                      providerName,
                      rewardCampaignId,
                      rewardCampaignName,
                    }),
                  });

                  if (canOpenPdpModal && product) {
                    openProductDetail(product, null);
                  }
                }
              : undefined,
          },
          resolveCalculated,
        );
      });
  }, [items, pathname, openProductDetail, resolve, resolveCalculated]);
};

export const ProductCardGridComponent: React.FC<ComponentProductCardGrid> = ({
  id,
  items = [],
  layoutType = 'GRID',
  rewardToggleColour,
  rewardToggleLabel,
}) => {
  const pathname = usePathname() ?? '';
  const { openProductDetail, productDetailModal } = useProductDetailModal({
    componentUniqueKey: `ProductCardGrid_${id}`,
  });

  const cardItems = useProductCardGridItems({
    items,
    pathname,
    openProductDetail,
  });

  if (!cardItems.length) {
    return null;
  }

  return (
    <>
      <ProductCardGrid
        items={cardItems}
        layoutType={layoutType}
        rewardToggleColour={rewardToggleColour}
        rewardToggleLabel={rewardToggleLabel}
      />
      {productDetailModal}
    </>
  );
};
